import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AuthService } from '../services/auth/auth.service';
import { ConfigService } from '../services/config.service';
import { ConsoleLogsCaptureService } from '../services/console-logs-capture.service';
import { FallbackImageService } from '../services/fallback-image.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class LoginComponent {
  public isLoginDropdownVisible: boolean;

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private consoleLogsCapture: ConsoleLogsCaptureService,
    private fallbackImageService: FallbackImageService
  ) {
    this.isLoginDropdownVisible = false;
  }

  toggleLoginDropdown() {
    this.isLoginDropdownVisible = !this.isLoginDropdownVisible;
  }

  handleClickOutsideLogin() {
    this.isLoginDropdownVisible = false;
  }

  logout() {
    this.authService.logout();
  }

  getUserInfo() {
    return this.authService.userAuthInfo$;
  }

  /**
   * Get safe profile picture URL with fallback handling
   */
  getSafeProfilePictureUrl(profilePictureUrl: string, userName?: string): string {
    return this.fallbackImageService.getSafeImageUrl(profilePictureUrl, userName);
  }

  /**
   * Handle profile picture loading errors
   */
  onImageError(event: Event, profilePictureUrl: string, userName?: string): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      this.fallbackImageService.markUrlAsFailed(profilePictureUrl);
      target.src = this.fallbackImageService.generateInitialsAvatar(userName);
    }
  }

  /**
   * Check if the given URL is a data URL (Base64 encoded).
   * Angular's NgOptimizedImage doesn't support data URLs, so we need to use regular src for those.
   */
  isDataUrl(url: string): boolean {
    return url?.startsWith('data:') || false;
  }

  /**
   * Check if we're in a development environment
   */
  isDevelopmentEnvironment(): boolean {
    try {
      const environment = this.configService.getEnvironment();
      return environment === 'dev' || environment === 'development' || environment === 'local';
    } catch (error) {
      // Fallback to checking hostname if config service fails
      return window.location.hostname === 'localhost' ||
             window.location.hostname === '10.0.0.6' ||
             window.location.hostname.startsWith('127.0.0.1') ||
             window.location.hostname.includes('dev.') ||
             !this.configService.getConfig().production;
    }
  }

  /**
   * Copy console logs to clipboard
   */
  copyConsoleLogsToClipboard(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard();
  }

  /**
   * Copy all console logs to clipboard
   */
  copyAllConsoleLogs(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard();
  }

  /**
   * Copy only WebSocket/connection related logs (filtered)
   */
  copyWebSocketLogs(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard({
      searchTerms: ['WebSocket', 'STOMP', 'CONNECTION', 'CONNECT', 'SOCKET', 'ws'],
      excludeTerms: ['heartbeat', 'health-check'],
      maxLines: 200
    });
  }

  /**
   * Copy recent errors and warnings (last 30 seconds)
   */
  copyRecentErrors(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard({
      timeRangeSeconds: 30,
      levels: ['ERROR', 'WARN']
    });
  }

  /**
   * Copy logs from the last 60 seconds
   */
  copyLastMinuteLogs(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard({
      timeRangeSeconds: 60,
      maxLines: 200
    });
  }

  /**
   * Clear all captured console logs
   */
  clearConsoleLogs(): void {
    const logCount = this.consoleLogsCapture.getLogCount();
    this.consoleLogsCapture.clearLogs();

    // Show visual feedback
    const buttons = Array.from(document.querySelectorAll('button'));
    const button = buttons.find(btn => btn.textContent?.includes('Clear Logs'));
    if (button) {
      const originalText = button.textContent;
      button.textContent = `✓ Cleared ${logCount} logs!`;
      setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Check if console log recording is enabled
   */
  isRecordingEnabled(): boolean {
    return this.consoleLogsCapture.isRecordingEnabled();
  }

  /**
   * Toggle console log recording
   */
  toggleRecording(): void {
    if (this.isRecordingEnabled()) {
      this.consoleLogsCapture.disableRecording();
    } else {
      this.consoleLogsCapture.enableRecording();
    }
  }

  /**
   * Check if trace UI is enabled
   */
  isTraceUIEnabled(): boolean {
    return this.consoleLogsCapture.isTraceUIEnabled();
  }

  /**
   * Toggle trace UI visibility
   */
  toggleTraceUI(): void {
    if (this.isTraceUIEnabled()) {
      this.consoleLogsCapture.disableTraceUI();
    } else {
      this.consoleLogsCapture.enableTraceUI();
    }
  }

  /**
   * Worker Dashboard visibility
   */
  showWorkerDashboard = false;

  /**
   * Toggle Worker Dashboard overlay
   */
  toggleWorkerDashboard(): void {
    this.showWorkerDashboard = !this.showWorkerDashboard;
  }
}
